import {dateToString} from "@/utils/dateUtils";
import {computed, isRef, ref, type Ref, toRefs} from "vue";
import type {AddressFilled, ShippingRate} from "@/stores/cartStore";
import type {ShippingMethodExact, ShippingMethodGround} from "@/types/shipping.types";
import {getDeliveryDays, hasFixedDeliveryDays, hasDeliveryDaysEstimate, isNextDayRate, isSecondDayRate} from "@/utils/shippingRates";

interface UseDatePickerProps {
  initialDate: Ref<Date> | Date
  disabledShippingDates: Date[]
  disabledDeliveryDates: Date[]
  selectedAddress: Ref<AddressFilled> | AddressFilled
  itemWeight: number
  isExactDayDelivery: Ref<boolean> | boolean
  cutOffTime: string
  minDate: Date | null
  clickedDate: Ref<Date | null> | Date | null
  maxDate: Date | null
  rates: Ref<ShippingRate[]> | ShippingRate[]
}

interface UseDatePickerResult {
  getDayWithOffset: ( date: Date, moveDays?: number, skipDays?: "weekends" | "delivery" | "shipping" | "shipping_test" ) => Date
  firstAvailableShippingDate: Ref<Date>
  calculateRange: (clickedDate: Date | null, updateShipDate?: boolean) => Date[]
  selectedDates: Ref<Date[]>
  exactDayDelivery: Ref<ShippingMethodExact | null>
  exactDayShippingDate: Ref<Date | null>
  calcApplyDate: () => { shipDate?: Date, estimatedDate?: Date | [Date, Date], rate?: ShippingRate }
  isNextDaySelected: Ref<boolean | null>
  isSecondDaySelected: Ref<boolean | null>
  rangeShippingDate: Ref<Date | null>
  nextDayDelivery: Ref<ShippingMethodExact | null>
  secondDayDelivery: Ref<ShippingMethodExact | null>
  groundDelivery: Ref<ShippingMethodGround | null>
}

export function useDatePicker(props: UseDatePickerProps): UseDatePickerResult {
  const {
    disabledShippingDates,
    disabledDeliveryDates,
    itemWeight,
    cutOffTime,
    minDate,
    maxDate,
  } = toRefs(props);

  const rates = isRef(props.rates) ? props.rates : ref(props.rates);
  const clickedDate = isRef(props.clickedDate) ? props.clickedDate : ref(props.clickedDate);
  const isExactDayDelivery = isRef(props.isExactDayDelivery) ? props.isExactDayDelivery : ref(props.isExactDayDelivery);
  const initialDate = isRef(props.initialDate) ? props.initialDate : ref(props.initialDate);
  const selectedAddress = isRef(props.selectedAddress) ? props.selectedAddress : ref(props.selectedAddress);


  const matchedRates = computed(() => {
    const claimed: ShippingRate[] = [];

    const claim = (predicate: (rate: ShippingRate) => boolean) => {
      const rate = rates.value.find(i => !claimed.includes(i) && predicate(i));
      if(rate) {
        claimed.push(rate);
      }
      return rate;
    };

    const nextDayMatched = claim(isNextDayRate);
    const secondDayMatched = claim(isSecondDayRate);

    const nextDay = nextDayMatched || claim(i => hasFixedDeliveryDays(i, 1));
    const secondDay = secondDayMatched || claim(i => hasFixedDeliveryDays(i, 2));
    const ground = claim(i => hasDeliveryDaysEstimate(i) && !hasFixedDeliveryDays(i, 1) && !hasFixedDeliveryDays(i, 2));

    return { nextDay, secondDay, ground };
  });

  const groundDelivery = computed<ShippingMethodGround | null>(() => {
    const groundShipping = matchedRates.value.ground;
    const deliveryDays = groundShipping ? getDeliveryDays(groundShipping) : [];
    if(!groundShipping || !deliveryDays.length) {
      return null;
    }

    const minDays = Math.min(...deliveryDays);
    const maxDays = Math.max(...deliveryDays);

    return {
      type: 'ground',
      isOneTwo: minDays === 1 && maxDays === 2,
      days: [minDays, maxDays],
      price: parseFloat(groundShipping.price),
      rate: {
        ...groundShipping
      }
    };
  });

  const nextDayDelivery = computed<ShippingMethodExact | null>(() => {
    const nextDayShipping = matchedRates.value.nextDay;
    if(!nextDayShipping) {
      return null;
    }

    return {
      type: 'exact',
      day: getDayWithOffset(firstAvailableShippingDate.value, 1, "weekends"),
      days: 1,
      price: parseFloat(parseFloat(nextDayShipping.price || "0").toFixed(2)),
      extra: parseFloat((parseFloat(nextDayShipping.price || "0") - (groundDelivery.value?.price || 0)).toFixed(2)),
      rate: {
        ...nextDayShipping
      }
    };
  });

  const secondDayDelivery = computed<ShippingMethodExact | null>(() => {
    const secondDayShipping = matchedRates.value.secondDay;
    if(!secondDayShipping) {
      return null;
    }

    return {
      type: 'exact',
      day: getDayWithOffset(firstAvailableShippingDate.value, 2, "weekends"),
      days: 2,
      price: parseFloat(parseFloat(secondDayShipping.price).toFixed(2)),
      extra: parseFloat((parseFloat(secondDayShipping.price) - (groundDelivery.value?.price || 0)).toFixed(2)),
      rate: {
        ...secondDayShipping
      }
    };
  });

  const rangeShippingDate = ref<Date | null>(null);
  const exactDayDelivery = ref<ShippingMethodExact | null>(null);
  const exactDayShippingDate = ref<Date | null>(null);

  const isNextDaySelected = computed(() => nextDayDelivery.value && isExactDayDelivery.value && selectedDates.value.length === 1 && dateToString(selectedDates.value[0]) === dateToString(nextDayDelivery.value.day));
  const isSecondDaySelected = computed(() => secondDayDelivery.value && isExactDayDelivery.value &&  selectedDates.value.length === 1 && dateToString(selectedDates.value[0]) === dateToString(secondDayDelivery.value.day));

  const getDayWithOffset = (date: Date, moveDays = 0, skipDays: "weekends" | "delivery" | "shipping" | "shipping_test" = "delivery") => {
    const moveDirection = moveDays < 0 ? -1 : 1;
    const startDate = new Date(date);
    if(moveDays) {
      // move and skip weekends
      for(let i = 0; i < Math.abs(moveDays); i ++) {
        startDate.setDate(startDate.getDate() + moveDirection);
        while (
          startDate.getDay() === 0 ||
          ( skipDays === "weekends" || skipDays === "shipping" ||  skipDays === "shipping_test" ) && startDate.getDay() === 6 ||
          skipDays === "shipping" && disabledShippingDates.value.findIndex( i => dateToString(startDate) === dateToString(i) ) !== -1 ||
          ( skipDays === "delivery" ||  skipDays === "shipping_test") && disabledDeliveryDates.value.findIndex( i => dateToString(startDate) === dateToString(i) ) !== -1 ||
          skipDays === "delivery" && startDate.getDay() === 6 && isExactDayDelivery.value ||
          skipDays === "delivery" && startDate.getDay() === 6 && !isExactDayDelivery.value && ((itemWeight.value <= 1 && selectedAddress.value.type === "Commercial") || itemWeight.value > 1) ||
          skipDays === "shipping_test" && startDate.getDay() === 6 && ((itemWeight.value <= 1 && selectedAddress.value.type === "Commercial") || itemWeight.value > 1)
          )  {
          //skip weekends
          startDate.setDate(startDate.getDate() + moveDirection);
        }
      }
    } else {
      while(
        startDate.getDay() === 0 ||
        ( skipDays === "weekends" ||  skipDays === "shipping" ||  skipDays === "shipping_test" ) && startDate.getDay() === 6  ||
        skipDays === "shipping" && disabledShippingDates.value.findIndex( i => dateToString(startDate) === dateToString(i) ) !== -1 ||
        ( skipDays === "delivery" ||  skipDays === "shipping_test") && disabledDeliveryDates.value.findIndex( i => dateToString(startDate) === dateToString(i) ) !== -1 ||
        skipDays === "delivery" && startDate.getDay() === 6 && isExactDayDelivery.value ||
        skipDays === "delivery" && startDate.getDay() === 6 && !isExactDayDelivery.value && ((itemWeight.value <= 1 && selectedAddress.value.type === "Commercial") || itemWeight.value > 1) ||
        skipDays === "shipping_test" && startDate.getDay() === 6 && ((itemWeight.value <= 1 && selectedAddress.value.type === "Commercial") || itemWeight.value > 1)
        )  {
        startDate.setDate(startDate.getDate() + moveDirection);
      }
    }
    return startDate;
  }

  const firstAvailableShippingDate = computed<Date>(() => {
    const dateEST = new Date(
      initialDate.value.toLocaleString('en-US', { timeZone: 'America/New_York' })
    );

    let dateNow = new Date(
      initialDate.value.toLocaleString('en-US', { timeZone: 'America/New_York' })
    );

    const timeEST = dateEST.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    if(minDate.value) {
      const firstAvailableShippingDayInFuture = getDayWithOffset(minDate.value, -1, "shipping");

      if(dateToString(firstAvailableShippingDayInFuture) > dateToString(initialDate.value)) {
        return firstAvailableShippingDayInFuture;
      }
    }

    const dateNextToToday = new Date(dateNow);
    dateNextToToday.setDate(dateNextToToday.getDate() + 1);

    if(dateNextToToday.getDay() === 6) {
      return getDayWithOffset(dateNow, 1, "shipping");
    }

    if(
      dateNow.getDay() === 0 ||
      dateNow.getDay() === 6
    ) {
      //if weekends don't calculate cut-off time
      return getDayWithOffset(dateNow, 0, "shipping");
    }

    if(timeEST > cutOffTime.value) {
      return getDayWithOffset(dateNow, 2, "shipping");
    }

    return getDayWithOffset(dateNow, 1, "shipping");
  });

  const resolveExactDay = (delivery: ShippingMethodExact | null, targetDate: Date) => {
    if(!delivery) {
      return null;
    }

    const shipDate = getDayWithOffset(targetDate, -delivery.days, "shipping_test");
    const deliveryDate = getDayWithOffset(shipDate, delivery.days, "delivery");

    if(
      disabledShippingDates.value.findIndex( i => dateToString(shipDate) === dateToString(i) ) !== -1 ||
      dateToString(deliveryDate) !== dateToString(targetDate)
    ) {
      return null;
    }

    return { delivery, shipDate };
  };

  const calculateRange = (clickedDate: Date | null, updateShipDate = false) => {
    if(!clickedDate) {
      return [];
    }

    if(isExactDayDelivery.value && nextDayDelivery.value && dateToString(nextDayDelivery.value.day) === dateToString(clickedDate)) {
      //User selected first day delivery
      return [nextDayDelivery.value.day];
    }

    if(isExactDayDelivery.value && secondDayDelivery.value && dateToString(secondDayDelivery.value.day) === dateToString(clickedDate)) {
      //User selected next day delivery
      return [secondDayDelivery.value.day];
    }

    if(isExactDayDelivery.value) {
      const resolved = resolveExactDay(secondDayDelivery.value, clickedDate) || resolveExactDay(nextDayDelivery.value, clickedDate);

      if(updateShipDate) {
        exactDayDelivery.value = resolved && resolved.delivery || null;
        exactDayShippingDate.value = resolved && resolved.shipDate || null;
      }

      return resolved ? [clickedDate] : [];
    }

    if(selectedAddress.value && !groundDelivery.value && !isExactDayDelivery.value) {
      return [];
    }

    const maxDays = Math.max(...groundDelivery.value?.days || []);
    const minDays = Math.min(...groundDelivery.value?.days || []);
    const startDeliveryDay = getDayWithOffset(new Date(clickedDate), -( maxDays -1 ) );

    let firstDeliveryDate = getDayWithOffset(new Date(clickedDate), -( maxDays - minDays ));
    let lastDate = new Date(clickedDate);

    if(dateToString(getDayWithOffset(startDeliveryDay, -1)) > dateToString(firstAvailableShippingDate.value)) {
      let shipDate = getDayWithOffset(new Date(startDeliveryDay), -1, "shipping_test");

      while( shipDate.getDay() === 0 || shipDate.getDay() === 6 || disabledShippingDates.value.findIndex( i => dateToString(i) === dateToString(shipDate) ) !== -1 ) {
        firstDeliveryDate = getDayWithOffset(firstDeliveryDate, 1);
        lastDate = getDayWithOffset(lastDate, 1);
        shipDate = getDayWithOffset(shipDate, 1);
        if(maxDate.value && dateToString(getDayWithOffset(clickedDate, 1)) >= dateToString(maxDate.value)) {
          //disable move forward if max_date

          if(updateShipDate) {
            rangeShippingDate.value = null;
          }
          return [];
        }
      }

      if(updateShipDate) {
        rangeShippingDate.value = shipDate;
      }
      return [firstDeliveryDate, lastDate];
    } else {
      if(updateShipDate) {
        rangeShippingDate.value = new Date(firstAvailableShippingDate.value);
      }
      return [getDayWithOffset(new Date(firstAvailableShippingDate.value), minDays, "delivery"), getDayWithOffset(new Date(firstAvailableShippingDate.value), maxDays)];
    }
  }

  const selectedDates = computed(() => {
    return calculateRange(clickedDate.value, true);
  });

  const calcApplyDate = () => {

    let shipDate, rate, estimatedDate;

    if(clickedDate.value) {
      if(isExactDayDelivery.value) {
        if (isNextDaySelected.value && nextDayDelivery.value) {
          shipDate = new Date(firstAvailableShippingDate.value.toString());
          estimatedDate = new Date(nextDayDelivery.value.day.toString());
          rate = nextDayDelivery.value.rate;
        } else if (isSecondDaySelected.value && secondDayDelivery.value) {
          shipDate = new Date(firstAvailableShippingDate.value.toString());
          estimatedDate = new Date(secondDayDelivery.value.day.toString());
          rate = secondDayDelivery.value.rate;
        } else if (exactDayDelivery.value && exactDayShippingDate.value) {
          shipDate = new Date(exactDayShippingDate.value.toString());
          estimatedDate = new Date(clickedDate.value.toString());
          rate = exactDayDelivery.value.rate;
        }
      } else if(rangeShippingDate.value && groundDelivery.value) {
        shipDate = new Date(rangeShippingDate.value.toString());
        estimatedDate = [
          new Date(selectedDates.value[0].toString()),
          new Date(selectedDates.value[1].toString()),
        ] as [Date, Date];
        rate = groundDelivery.value.rate;
      }
    }

    return {
      estimatedDate,
      shipDate,
      rate,
    }
  }

  return {
    getDayWithOffset,
    firstAvailableShippingDate,
    calculateRange,
    selectedDates,
    exactDayDelivery,
    exactDayShippingDate,
    calcApplyDate,
    isNextDaySelected,
    isSecondDaySelected,
    rangeShippingDate,
    groundDelivery,
    nextDayDelivery,
    secondDayDelivery
  }
}